import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getSubscriptionStatus, createTrialSubscription, migrateTrialToStripe } from '../services/stripeService';

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  return context;
};

// Subscription tiers configuration
export const SUBSCRIPTION_TIERS = {
  LITE: {
    id: 'lite',
    name: 'Lite',
    price: 199.99,
    vehicleLimit: null, // No cap
    features: [
      'Unlimited active vehicles',
      'Basic vehicle management',
      'Invoice generation',
      'Customer contact management',
      'Basic reporting',
      'Email support'
    ]
  },
  PREMIUM: {
    id: 'premium',
    name: 'Premium',
    price: 299.99,
    vehicleLimit: null, // Unlimited
    features: [
      'Unlimited vehicles',
      'Advanced vehicle management',
      'Invoice generation',
      'Customer contact management',
      'Web Booking tool',
      'Car Sales Module',
      'Customer Portal',
      'Advanced reporting & analytics',
      'Vehicle listing management',
      'Priority support',
      'Data export capabilities',
      'Custom branding options'
    ]
  }
};

export const SubscriptionProvider = ({ children }) => {
  const [subscription, setSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  
  // Track if a sync is in progress to prevent concurrent calls
  const syncInProgressRef = useRef(false);
  const lastSyncTimeRef = useRef(0);

  // Get user ID from sessionStorage
  const getUserId = () => {
    return sessionStorage.getItem('userId') || sessionStorage.getItem('firebaseUid');
  };

  // Helper function to sleep for retry delays
  const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  // Update subscription state from Stripe data
  const updateSubscriptionFromStripe = useCallback((stripeData) => {
    if (!stripeData.subscription) return null;

    const stripeSub = stripeData.subscription;
    const priceId = stripeSub.plan?.id;
    // Use exact price IDs from config
    const STRIPE_PRICE_ID_PREMIUM = 'price_1SlXb7BS4SYXcdFY8C90NDmQ';
    const isPremium = priceId === STRIPE_PRICE_ID_PREMIUM || priceId === process.env.REACT_APP_STRIPE_PRICE_ID_PREMIUM;
    const tier = isPremium ? 'premium' : 'lite';

    // Check if subscription is in trial period
    const isTrialing = stripeSub.status === 'trialing';
    const trialStart = stripeSub.trial_start ? new Date(stripeSub.trial_start * 1000) : null;
    const trialEnd = stripeSub.trial_end ? new Date(stripeSub.trial_end * 1000) : null;

    // Format invoices for billing history
    const billingHistory = stripeData.invoices ? stripeData.invoices.map(invoice => ({
      id: invoice.id,
      date: new Date(invoice.created * 1000).toISOString(),
      type: invoice.status === 'paid' ? 'Payment' : invoice.status === 'open' ? 'Pending' : 'Failed',
      amount: (invoice.amount_paid || invoice.amount_due) / 100, // Convert from pence to pounds
      balance: invoice.status === 'paid' ? 0 : (invoice.amount_due || 0) / 100,
      reference: invoice.number || invoice.id,
      invoicePdf: invoice.invoice_pdf,
      hostedInvoiceUrl: invoice.hosted_invoice_url,
      status: invoice.status,
    })) : [];

    const updatedSubscription = {
      tier: tier,
      status: stripeSub.status,
      stripeSubscriptionId: stripeSub.id,
      stripeCustomerId: stripeData.customer?.id,
      currentPeriodStart: stripeSub.current_period_start 
        ? new Date(stripeSub.current_period_start * 1000).toISOString()
        : new Date().toISOString(),
      currentPeriodEnd: stripeSub.current_period_end
        ? new Date(stripeSub.current_period_end * 1000).toISOString()
        : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      nextBillingDate: stripeSub.current_period_end
        ? new Date(stripeSub.current_period_end * 1000).toISOString()
        : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      cancelAtPeriodEnd: stripeSub.cancel_at_period_end || false,
      isTrialPeriod: isTrialing,
      trialStart: trialStart ? trialStart.toISOString() : null,
      trialEndsAt: trialEnd ? trialEnd.toISOString() : null,
      syncedFromStripe: true,
      lastSyncedAt: new Date().toISOString(),
      billingHistory: billingHistory,
    };

    setSubscription(updatedSubscription);
    return updatedSubscription;
  }, []);

  // Sync subscription from Stripe with rate limit handling
  const syncSubscriptionFromStripe = useCallback(async (userId, retryCount = 0) => {
    if (!userId) return null;

    // Prevent concurrent calls - if already syncing, return early
    if (syncInProgressRef.current) {
      console.log('Sync already in progress, skipping duplicate call');
      return null;
    }

    // Debounce: if called within last 2 seconds, skip to prevent rate limits
    const now = Date.now();
    const timeSinceLastSync = now - lastSyncTimeRef.current;
    if (timeSinceLastSync < 2000 && lastSyncTimeRef.current > 0) {
      console.log('Sync called too soon after last sync, skipping to prevent rate limits');
      return null;
    }

    syncInProgressRef.current = true;
    lastSyncTimeRef.current = now;

    try {
      setIsSyncing(true);
      const stripeData = await getSubscriptionStatus(userId);
      
      if (stripeData.subscription) {
        return updateSubscriptionFromStripe(stripeData);
      }
      return null;
    } catch (error) {
      // Handle rate limit errors with exponential backoff
      const isRateLimitError = error.message && (
        error.message.includes('rate limit') || 
        error.message.includes('Request rate limit exceeded')
      );

      if (isRateLimitError && retryCount < 3) {
        const backoffDelay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
        console.warn(`Rate limit hit, retrying in ${backoffDelay}ms (attempt ${retryCount + 1}/3)`);
        await sleep(backoffDelay);
        syncInProgressRef.current = false;
        return syncSubscriptionFromStripe(userId, retryCount + 1);
      }

      console.error('Error syncing subscription from Stripe:', error);
      // If it's a rate limit error and we've exhausted retries, show user-friendly message
      if (isRateLimitError) {
        console.warn('Rate limit exceeded. Please wait a moment before trying again.');
      }
      return null;
    } finally {
      setIsSyncing(false);
      syncInProgressRef.current = false;
    }
  }, [updateSubscriptionFromStripe]);

  // Migrate localStorage trial to Stripe
  const migrateTrialToStripeHandler = useCallback(async (localStorageTrial) => {
    try {
      const userId = getUserId();
      const userEmail = sessionStorage.getItem('userEmail');
      
      if (!userId) {
        console.error('Cannot migrate trial: userId not found');
        return false;
      }

      const result = await migrateTrialToStripe(userId, userEmail, localStorageTrial.trialEndsAt);
      
      if (result.success) {
        // Clear localStorage after successful migration
        localStorage.removeItem('userSubscription');
        // Sync the migrated subscription
        await syncSubscriptionFromStripe(userId);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error migrating trial to Stripe:', error);
      return false;
    }
  }, [syncSubscriptionFromStripe]);

  // Load subscription data - check Firestore/Stripe first, fallback to localStorage for migration
  useEffect(() => {
    const loadSubscription = async () => {
      try {
        const userId = getUserId();
        
        // Try to sync from Stripe/Firestore first
        if (userId) {
          const stripeSubscription = await syncSubscriptionFromStripe(userId);
          if (stripeSubscription) {
            // Found Stripe subscription - clear localStorage
            localStorage.removeItem('userSubscription');
            setIsLoading(false);
            return;
          }
        }

        // Check for localStorage trial to migrate
        const stored = localStorage.getItem('userSubscription');
        if (stored) {
          const parsed = JSON.parse(stored);
          // Only migrate if not already synced from Stripe
          if (!parsed.syncedFromStripe && parsed.isTrialPeriod) {
            // Try to migrate localStorage trial to Stripe
            const migrated = await migrateTrialToStripeHandler(parsed);
            if (migrated) {
              // Migration successful, subscription will be loaded from Stripe
              setIsLoading(false);
              return;
            }
            // Migration failed, use localStorage temporarily
            setSubscription(parsed);
          } else if (!parsed.syncedFromStripe) {
            setSubscription(parsed);
          }
        } else {
          // No subscription found - create new trial for new users
          if (userId) {
            try {
              const userEmail = sessionStorage.getItem('userEmail');
              const trialResult = await createTrialSubscription(userId, userEmail);
              
              if (trialResult.subscription) {
                // Trial created, sync it
                await syncSubscriptionFromStripe(userId);
                return;
              }
            } catch (trialError) {
              const msg = (trialError?.message || '').toLowerCase();
              const isConfigError = msg.includes('stripe_secret_key') || msg.includes('is required');
              if (!isConfigError) {
                console.error('Error creating trial subscription:', trialError);
              }
              // Continue to fallback (no subscription / trial unavailable)
            }
          }
          
          // Fallback: No subscription available
          setSubscription(null);
        }
      } catch (err) {
        console.error('Error loading subscription:', err);
        setSubscription(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadSubscription();
  }, [syncSubscriptionFromStripe, migrateTrialToStripeHandler]);

  // Save subscription to localStorage only if not synced from Stripe (for migration period)
  useEffect(() => {
    if (!isLoading && subscription && !subscription.syncedFromStripe) {
      localStorage.setItem('userSubscription', JSON.stringify(subscription));
    } else if (!isLoading && subscription && subscription.syncedFromStripe) {
      // Clear localStorage for Stripe-synced subscriptions
      localStorage.removeItem('userSubscription');
    }
  }, [subscription, isLoading]);

  const getCurrentTier = () => {
    if (!subscription) return SUBSCRIPTION_TIERS.LITE;
    return SUBSCRIPTION_TIERS[subscription.tier.toUpperCase()] || SUBSCRIPTION_TIERS.LITE;
  };

  const isLiteTier = () => {
    return getCurrentTier().id === 'lite';
  };

  const isPremiumTier = () => {
    return getCurrentTier().id === 'premium';
  };

  const getVehicleLimit = () => {
    return getCurrentTier().vehicleLimit;
  };

  const canAddVehicle = (currentVehicleCount) => {
    const limit = getVehicleLimit();
    if (limit === null) return true; // Unlimited
    return currentVehicleCount < limit;
  };

  const getRemainingVehicles = (currentVehicleCount) => {
    const limit = getVehicleLimit();
    if (limit === null) return null; // Unlimited
    return Math.max(0, limit - currentVehicleCount);
  };

  const upgradeToPremium = () => {
    const updatedSubscription = {
      ...subscription,
      tier: SUBSCRIPTION_TIERS.PREMIUM.id,
      upgradeDate: new Date().toISOString(),
      isTrialPeriod: false
    };
    setSubscription(updatedSubscription);
  };

  const downgradeToLite = () => {
    const updatedSubscription = {
      ...subscription,
      tier: SUBSCRIPTION_TIERS.LITE.id,
      downgradeDate: new Date().toISOString(),
      isTrialPeriod: false
    };
    setSubscription(updatedSubscription);
  };

  const updatePaymentMethod = (paymentMethod) => {
    const updatedSubscription = {
      ...subscription,
      paymentMethod,
      isTrialPeriod: false
    };
    setSubscription(updatedSubscription);
  };

  const isTrialActive = () => {
    if (!subscription) return false;
    
    // Check if subscription is in trialing status (from Stripe)
    if (subscription.status === 'trialing') {
      return true;
    }
    
    // Check localStorage trial (for migration period)
    if (subscription.isTrialPeriod && subscription.trialEndsAt) {
      const trialEnd = new Date(subscription.trialEndsAt);
      return new Date() < trialEnd;
    }
    
    return false;
  };

  const getTrialDaysRemaining = () => {
    if (!subscription) return 0;
    
    // Use server-side trial end date if available
    if (subscription.trialEndsAt) {
      const trialEnd = new Date(subscription.trialEndsAt);
      const now = new Date();
      const diffTime = trialEnd - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return Math.max(0, diffDays);
    }
    
    // If status is trialing but no trialEndsAt, return 0 (shouldn't happen)
    if (subscription.status === 'trialing') {
      return 0; // Will be updated on next sync
    }
    
    return 0;
  };

  const value = {
    subscription,
    setSubscription,
    isLoading,
    isSyncing,
    getCurrentTier,
    isLiteTier,
    isPremiumTier,
    getVehicleLimit,
    canAddVehicle,
    getRemainingVehicles,
    upgradeToPremium,
    downgradeToLite,
    updatePaymentMethod,
    isTrialActive,
    getTrialDaysRemaining,
    syncSubscriptionFromStripe,
    updateSubscriptionFromStripe,
    migrateTrialToStripe: migrateTrialToStripeHandler,
    createTrialSubscription,
    SUBSCRIPTION_TIERS
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};








